import {useEffect, useRef, useState} from "react";
import {QrCode} from "./qr";
import type {AnswerPhoto} from "./activity-library";
import {AnnotatedPhoto, PhotoAnnotationEditor} from "./PhotoAnnotationEditor";
import type {PhotoAnnotation, PhotoStroke} from "./photo-annotations";

type PhotoSession = {sessionId: string; url: string; uploadPath: string; expiresAt: string; replacePhotoId?: string | null};
async function request<T>(url: string, body?: unknown): Promise<T> {
  const response = await fetch(url, body === undefined ? {cache: "no-store"} : {
    method: "POST", headers: {"Content-Type": "application/json"}, body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error ?? "Envoi photo impossible.");
  return result as T;
}
async function deletePhotoRequest(url: string): Promise<void> {
  const response = await fetch(url, {method: "DELETE", headers: {"Accept": "application/json"}});
  const result = await response.json();
  if (!response.ok) throw new Error(result.error ?? "Suppression impossible.");
}
async function jpegExifOrientation(file: File): Promise<number> {
  if (file.type.toLowerCase() !== "image/jpeg") return 1;
  try {
    const buffer = await file.arrayBuffer();
    const view = new DataView(buffer);
    if (view.byteLength < 12 || view.getUint16(0, false) !== 0xffd8) return 1;
    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      const marker = view.getUint16(offset, false);
      if ((marker & 0xff00) !== 0xff00) break;
      if (marker === 0xffda || marker === 0xffd9) break;
      const length = view.getUint16(offset + 2, false);
      if (length < 2 || offset + 2 + length > view.byteLength) break;
      if (marker === 0xffe1 && length >= 10 && view.getUint32(offset + 4, false) === 0x45786966) {
        const tiff = offset + 10;
        if (tiff + 8 > view.byteLength) return 1;
        const endian = view.getUint16(tiff, false);
        const little = endian === 0x4949;
        if (!little && endian !== 0x4d4d) return 1;
        const ifd = tiff + view.getUint32(tiff + 4, little);
        if (ifd + 2 > view.byteLength) return 1;
        const count = view.getUint16(ifd, little);
        for (let i = 0; i < count; i++) {
          const entry = ifd + 2 + i * 12;
          if (entry + 12 > view.byteLength) break;
          if (view.getUint16(entry, little) === 0x0112) {
            const orientation = view.getUint16(entry + 8, little);
            return orientation >= 1 && orientation <= 8 ? orientation : 1;
          }
        }
        return 1;
      }
      offset += 2 + length;
    }
  } catch {
    // Corrupt or unusual EXIF metadata: decode normally instead of rejecting the photo.
  }
  return 1;
}

function encodedJpeg(source: CanvasImageSource, width: number, height: number, orientation: number, file: File) {
  const swapsAxes = orientation >= 5 && orientation <= 8;
  const orientedWidth = swapsAxes ? height : width;
  const orientedHeight = swapsAxes ? width : height;
  const ratio = Math.min(1, 1800 / Math.max(orientedWidth, orientedHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(orientedWidth * ratio));
  canvas.height = Math.max(1, Math.round(orientedHeight * ratio));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Préparation de l’image indisponible.");
  context.scale(ratio, ratio);
  switch (orientation) {
    case 2: context.transform(-1, 0, 0, 1, width, 0); break;
    case 3: context.transform(-1, 0, 0, -1, width, height); break;
    case 4: context.transform(1, 0, 0, -1, 0, height); break;
    case 5: context.transform(0, 1, 1, 0, 0, 0); break;
    case 6: context.transform(0, 1, -1, 0, height, 0); break;
    case 7: context.transform(0, -1, -1, 0, height, width); break;
    case 8: context.transform(0, -1, 1, 0, 0, width); break;
  }
  context.drawImage(source, 0, 0, width, height);
  return {
    name: `${file.name.replace(/\.[^.]+$/, "") || "photo"}.jpg`,
    mimeType: "image/jpeg",
    data: canvas.toDataURL("image/jpeg", .86).split(",")[1],
  };
}

async function prepare(file: File) {
  if (!file.type.startsWith("image/")) throw new Error("Choisis une image.");
  const orientation = await jpegExifOrientation(file);

  // Decode the raw JPEG pixels, then apply EXIF ourselves. This avoids Android/Samsung
  // browsers that inconsistently honor imageOrientation before canvas recompression.
  if ("createImageBitmap" in window) {
    try {
      const bitmap = await createImageBitmap(file, {imageOrientation: "none"});
      try {
        return encodedJpeg(bitmap, bitmap.width, bitmap.height, orientation, file);
      } finally {
        bitmap.close();
      }
    } catch {
      // Older browsers fall back to the HTML image decoder below. That decoder generally
      // applies EXIF itself, so do not apply the orientation a second time.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {image.onload=()=>resolve(); image.onerror=()=>reject(new Error("Image illisible.")); image.src=url;});
    return encodedJpeg(image, image.naturalWidth, image.naturalHeight, 1, file);
  } finally { URL.revokeObjectURL(url); }
}

export function LibraryAnswerPhotos({attemptId, previewActivityKey, blockId, photos: answerPhotos = [], writable, disabled, beforeUpload, onRefresh, onActive, onExternalInteraction, annotations, canAnnotate = false, onAnnotate, canRotate = false, onRotate}: {
  attemptId?: string; previewActivityKey?: string; blockId: string; photos?: AnswerPhoto[]; writable: boolean; disabled: boolean;
  beforeUpload: () => Promise<void>; onRefresh: () => Promise<void>; onActive: (active: boolean) => void;
  onExternalInteraction?: () => void;
  annotations?: Record<string, PhotoAnnotation>; canAnnotate?: boolean;
  onAnnotate?: (photoId: string, strokes: PhotoStroke[]) => Promise<void>;
  canRotate?: boolean; onRotate?: (photoId: string) => Promise<void>;
}) {
  const camera = useRef<HTMLInputElement>(null), fileInput = useRef<HTMLInputElement>(null);
  const [session, setSession] = useState<PhotoSession | null>(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<AnswerPhoto | null>(null);
  const [replacePhotoId, setReplacePhotoId] = useState<string | null>(null);
  const callbacks = useRef({onRefresh, onActive});
  callbacks.current = {onRefresh, onActive};
  const [previewPhotos,setPreviewPhotos]=useState<AnswerPhoto[]>([]);
  const previewOnly=!attemptId && !!previewActivityKey;
  const photos=previewOnly?previewPhotos:answerPhotos;
  async function receivedPhoto(result:{photo?:AnswerPhoto|null}) {
    if(previewOnly && result.photo){const photo=result.photo;setPreviewPhotos(previous=>previous.some(p=>p.id===photo.id)?previous:[...previous,photo]);}
    else await callbacks.current.onRefresh();
  }
  const replacementPhoto = replacePhotoId ? photos.find(photo => photo.id === replacePhotoId) ?? null : null;

  useEffect(() => {
    if (!session) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      try {
        const result = await request<{status: string;photo?:AnswerPhoto|null}>(`/api/interactive-photo/sessions/${encodeURIComponent(session.sessionId)}`);
        if (stopped) return;
        if (result.status === "completed") {
          await receivedPhoto(result);
          if (stopped) return;
          setMessage(previewOnly?"Photo d’essai reçue dans l’aperçu.":session.replacePhotoId ? "Photo remplacée dans ton travail." : "Photo enregistrée dans ton travail.");
          setSession(null);
          callbacks.current.onActive(false);
          return;
        }
        if (Date.now() >= Date.parse(session.expiresAt)) {
          setMessage(session.replacePhotoId ? "QR expiré. L’ancienne photo a été conservée." : "QR expiré. Tu peux en créer un nouveau.");
          setSession(null);
          callbacks.current.onActive(false);
          return;
        }
      } catch (error) { if (!stopped) setMessage(error instanceof Error ? error.message : "Connexion interrompue."); }
      if (!stopped) timer = setTimeout(check, 1800);
    };
    void check();
    return () => {stopped=true; clearTimeout(timer);};
  }, [session]);

  async function start(file?: File, replacementId = replacementPhoto?.id ?? null) {
    if (sending || disabled || (!attemptId && !previewActivityKey)) return;
    setSending(true); onActive(true); setMessage(replacementId ? "Préparation du remplacement…" : "Préparation de l’envoi…");
    try {
      const prepared = file ? await prepare(file) : null;
      await beforeUpload();
      const created = await request<PhotoSession>("/api/interactive-photo/sessions", {
        ...(attemptId?{attemptId}:{activityKey:previewActivityKey}),
        blockId,
        ...(replacementId ? {replacePhotoId: replacementId} : {}),
      });
      if (prepared) {
        await request(created.uploadPath, prepared);
        if(previewOnly)await receivedPhoto(await request<{photo?:AnswerPhoto|null}>(`/api/interactive-photo/sessions/${encodeURIComponent(created.sessionId)}`));
        else await onRefresh();
        setReplacePhotoId(null);
        setMessage(previewOnly?"Photo d’essai reçue dans l’aperçu.":replacementId ? "Photo remplacée dans ton travail." : "Photo enregistrée dans ton travail.");
        onActive(false);
      } else {
        setSession({...created, replacePhotoId: replacementId ?? null});
        setReplacePhotoId(null);
        setMessage(replacementId
          ? "Scanne le QR puis valide la nouvelle photo. L’ancienne reste conservée tant que l’envoi n’est pas terminé."
          : "Scanne le QR puis valide l’envoi sur ton téléphone.");
      }
    } catch (error) {setMessage(error instanceof Error ? error.message : "Envoi impossible."); onActive(false);}
    finally {setSending(false);}
  }

  async function removePhoto(photo: AnswerPhoto) {
    if (sending || disabled || session) return;
    if (!window.confirm("Supprimer cette photo ? Cette action est définitive.")) return;
    setSending(true); onActive(true); setMessage("Suppression de la photo…");
    try {
      await beforeUpload();
      await deletePhotoRequest(photo.url);
      if (selectedPhoto?.id === photo.id) setSelectedPhoto(null);
      if (replacePhotoId === photo.id) setReplacePhotoId(null);
      await onRefresh();
      setMessage("Photo supprimée. Tu peux en ajouter une autre.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Suppression impossible.");
    } finally {
      setSending(false); onActive(false);
    }
  }

  async function rotatePhoto(photo: AnswerPhoto) {
    if (!canRotate || !onRotate || sending || disabled || session) return;
    setSending(true); onActive(true); setMessage("Rotation de la photo…");
    try {
      if (selectedPhoto?.id === photo.id) setSelectedPhoto(null);
      await onRotate(photo.id);
      setMessage("Photo tournée de 90°.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Rotation impossible.");
    } finally {
      setSending(false); onActive(false);
    }
  }

  const choose = (files: FileList | null) => {if (files?.[0]) void start(files[0]);};
  if (!writable && !photos.length) return null;
  return <section className="library-answer-photos" aria-label="Photos de la réponse">
    {photos.length > 0 && <div className="library-photo-gallery">{photos.map(photo => <figure key={photo.id}>
      <button type="button" className="library-photo-open" disabled={disabled} aria-label={`${canAnnotate?"Corriger":"Voir"} la photo ${photo.name}`} onClick={()=>setSelectedPhoto(photo)}>
        <AnnotatedPhoto photo={photo} annotation={annotations?.[photo.id]} />
      </button>
      <figcaption>{photo.name} · {new Date(photo.createdAt).toLocaleString("fr-FR")}</figcaption>
      {!previewOnly && (writable || canRotate) && <div className="library-photo-item-actions">
        {canRotate && <button type="button" className="photo-rotate-button" disabled={disabled || sending || !!session} onClick={()=>void rotatePhoto(photo)}>↻ Tourner 90°</button>}
        {writable && <>
          <button type="button" disabled={disabled || sending || !!session} onClick={()=>{setReplacePhotoId(photo.id);setMessage("");}}>Remplacer</button>
          <button type="button" className="danger" disabled={disabled || sending || !!session} onClick={()=>void removePhoto(photo)}>Supprimer</button>
        </>}
      </div>}
    </figure>)}</div>}
    {selectedPhoto && <PhotoAnnotationEditor photo={selectedPhoto} annotation={annotations?.[selectedPhoto.id]} editable={canAnnotate}
      onSave={async strokes=>{if(!onAnnotate) throw new Error("Enregistrement indisponible.");await onAnnotate(selectedPhoto.id,strokes);}}
      onClose={()=>setSelectedPhoto(null)} />}

    {writable && replacementPhoto && !session && <div className="library-photo-replace">
      <p><strong>Remplacer « {replacementPhoto.name} »</strong></p>
      <p>L’ancienne photo restera en place jusqu’à ce que la nouvelle soit bien envoyée.</p>
      <div className="library-photo-actions">
        <button type="button" disabled={disabled || sending} onClick={()=>{onExternalInteraction?.();camera.current?.click();}}><span aria-hidden="true">📷 </span>Prendre une photo</button>
        <button type="button" disabled={disabled || sending} onClick={()=>{onExternalInteraction?.();fileInput.current?.click();}}>Importer une image</button>
        <button type="button" disabled={disabled || sending} onClick={()=>void start(undefined, replacementPhoto.id)}><span aria-hidden="true">▦ </span>Utiliser le téléphone par QR</button>
        <button type="button" className="secondary" disabled={disabled || sending} onClick={()=>setReplacePhotoId(null)}>Annuler</button>
      </div>
    </div>}

    {writable && !replacementPhoto && photos.length < 6 && <>
      <div className="library-photo-actions">
        <button type="button" disabled={disabled || sending || !!session} onClick={()=>{onExternalInteraction?.();camera.current?.click();}}><span aria-hidden="true">📷 </span>Prendre une photo</button>
        <button type="button" disabled={disabled || sending || !!session} onClick={()=>{onExternalInteraction?.();fileInput.current?.click();}}>Importer une image</button>
        <button type="button" disabled={disabled || sending || !!session} onClick={()=>void start(undefined, null)}><span aria-hidden="true">▦ </span>Utiliser le téléphone par QR</button>
      </div>
    </>}

    {writable && <>
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={e=>{choose(e.target.files); e.target.value="";}} />
      <input ref={fileInput} type="file" accept="image/*" hidden onChange={e=>{choose(e.target.files); e.target.value="";}} />
    </>}

    {session && <div className="library-photo-qr">
      <QrCode value={session.url} size={220} title={session.replacePhotoId ? "QR code pour remplacer la photo de cette réponse" : "QR code pour envoyer la photo de cette réponse"} />
      <a href={session.url} target="_blank" rel="noopener noreferrer">Ouvrir la page d’envoi</a>
      <p>{session.replacePhotoId
        ? "Le QR expire après 10 minutes. L’ancienne photo n’est supprimée qu’après réception de la nouvelle."
        : "Le QR expire après 10 minutes. La photo sera conservée même si tu fermes cette page."}</p>
      <button type="button" onClick={()=>{setSession(null); onActive(false); setMessage(session.replacePhotoId ? "QR masqué. L’ancienne photo reste conservée tant qu’aucun nouvel envoi n’est terminé." : "QR masqué. Un envoi déjà engagé reste possible jusqu’à expiration ou remise du travail. Actualise pour retrouver la photo.");}}>Masquer le QR</button>
    </div>}
    {message && <p role="status">{message}</p>}
  </section>;
}
